// Локальный синтез речи средствами браузера (Web Speech API / SpeechSynthesis).
// Это НЕ сторонний облачный сервис: озвучка идёт на самом устройстве (на iOS —
// голосами системы/Siri), ничего не уходит наружу. Используется как запасной
// путь, когда бэкенд присылает только текст ответа без готового аудио.

export interface SpeechOptions {
  lang: string
  rate: number
  pitch: number
  /** Уровень «говорения» 0..1 — для анимации волн, пока агент озвучивается. */
  onLevel?: (level: number) => void
}

export class SpeechSynthesizer {
  private opts: SpeechOptions
  private voice: SpeechSynthesisVoice | null = null
  private supported: boolean

  constructor(opts: SpeechOptions) {
    this.opts = opts
    this.supported = typeof window !== 'undefined' && 'speechSynthesis' in window
    if (this.supported) this.loadVoice()
  }

  get isSupported(): boolean {
    return this.supported
  }

  private loadVoice(): void {
    const pick = () => {
      const voices = window.speechSynthesis.getVoices()
      const lang = this.opts.lang.toLowerCase()
      const short = lang.slice(0, 2)
      const candidates = voices.filter((v) => v.lang.toLowerCase().startsWith(short))
      if (candidates.length === 0) {
        this.voice = null
        return
      }
      // Предпочитаем качественные голоса (Enhanced/Premium/Siri/Neural),
      // штрафуем «компактные» роботные. На iOS «Улучшенный» голос звучит сильно
      // лучше — его стоит догрузить в настройках системы.
      const score = (v: SpeechSynthesisVoice): number => {
        const n = `${v.name} ${v.voiceURI}`.toLowerCase()
        let s = 0
        if (/(enhanced|premium|neural|siri)/.test(n)) s += 4
        if (/compact/.test(n)) s -= 2
        if (v.lang.toLowerCase() === lang) s += 1
        if (v.localService) s += 0.5
        return s
      }
      candidates.sort((a, b) => score(b) - score(a))
      this.voice = candidates[0]
    }
    pick()
    // Список голосов в части браузеров грузится асинхронно.
    window.speechSynthesis.onvoiceschanged = pick
  }

  /**
   * «Разблокировка» в пользовательском жесте (тап). iOS Safari требует, чтобы
   * первый вызов синтеза произошёл из обработчика жеста — иначе последующие
   * программные вызовы молчат.
   */
  unlock(): void {
    if (!this.supported) return
    try {
      if (window.speechSynthesis.paused) window.speechSynthesis.resume()
      // Тихая, но НЕ пустая реплика, проигранная целиком в пользовательском жесте,
      // «будит» синтез на iOS — иначе первый реальный ответ не озвучивается.
      const u = new SpeechSynthesisUtterance(' ')
      u.volume = 0
      u.rate = 2
      if (this.voice) u.voice = this.voice
      window.speechSynthesis.speak(u)
    } catch {
      /* noop */
    }
  }

  speak(text: string, onEnd?: () => void, rate?: number): void {
    if (!this.supported) {
      onEnd?.()
      return
    }
    const trimmed = text.trim()
    if (!trimmed) {
      onEnd?.()
      return
    }
    this.cancel()
    if (window.speechSynthesis.paused) window.speechSynthesis.resume()

    const u = new SpeechSynthesisUtterance(trimmed)
    u.lang = this.opts.lang
    u.rate = rate && rate > 0 ? rate : this.opts.rate
    u.pitch = this.opts.pitch
    if (this.voice) u.voice = this.voice

    u.onstart = () => this.opts.onLevel?.(0.5)
    u.onboundary = () => this.opts.onLevel?.(0.3 + Math.random() * 0.45)
    u.onend = () => {
      this.opts.onLevel?.(0)
      onEnd?.()
    }
    u.onerror = () => {
      this.opts.onLevel?.(0)
      onEnd?.()
    }

    window.speechSynthesis.speak(u)
  }

  cancel(): void {
    if (!this.supported) return
    try {
      window.speechSynthesis.cancel()
    } catch {
      /* noop */
    }
    this.opts.onLevel?.(0)
  }

  get isSpeaking(): boolean {
    return this.supported && window.speechSynthesis.speaking
  }

  close(): void {
    this.cancel()
  }
}
